// Feb 2026 — Canonical jurisdiction-type taxonomy.
//
// Single source of truth for every "Jurisdiction Type" / "Authority Type"
// dropdown in the app. Replaces the legacy hardcoded short lists that
// had drifted across multiple components (some 5-item with `school` +
// `other`, some 6-item, some 7-item, etc.) — causing role-scoped
// inconsistencies on the order Fulfillment page (Admin saw the full
// taxonomy, Tax Team Leader / Tax Processor saw the legacy short list).
//
// Keep this list in sync with the backend `Jurisdictions` taxonomy and
// the `JURISDICTION_TYPES` constant inside
// `components/jurisdiction/JurisdictionWizardModal.jsx` (the canonical
// entry-point used by the Admin Jurisdiction Directory creator).
//
// LEGACY VALUE HANDLING — `school`, `state`, `other`, `municipality`:
// Historical records may carry these values. Components should NOT
// expose them as selectable options going forward, but MUST still
// render the saved label so historical records remain legible. Use
// `getLegacyJurisdictionTypeLabel(value)` for that fallback display.

export const JURISDICTION_TYPE_OPTIONS = Object.freeze([
  { value: 'county',           label: 'County' },
  { value: 'city',             label: 'City' },
  { value: 'town',             label: 'Town' },
  { value: 'township',         label: 'Township' },
  { value: 'parish',           label: 'Parish' },
  { value: 'borough',          label: 'Borough' },
  { value: 'village',          label: 'Village' },
  { value: 'school_district',  label: 'School District' },
  { value: 'special_district', label: 'Special District' },
]);

// Lowercase set used by validators / matchers.
export const JURISDICTION_TYPE_VALUES = Object.freeze(
  JURISDICTION_TYPE_OPTIONS.map((o) => o.value)
);

// Pre-built lookup so callers don't have to re-`.find()` on every render.
const VALUE_TO_LABEL = Object.freeze(
  JURISDICTION_TYPE_OPTIONS.reduce((acc, o) => {
    acc[o.value] = o.label;
    return acc;
  }, {})
);

// Legacy values that we still display (for historical records) but
// never offer as new selections. Map to a human-friendly title-case
// label so older records keep reading clean in the UI.
const LEGACY_LABELS = Object.freeze({
  school: 'School (legacy)',
  state: 'State (legacy)',
  other: 'Other (legacy)',
  municipality: 'Municipality (legacy)',
});

/**
 * Resolve a label for any jurisdiction-type value — canonical OR legacy.
 * @param {string} value raw saved value (e.g. 'county', 'school', 'other')
 * @returns {string} label suitable for display, or the raw value when unrecognised
 */
export function getJurisdictionTypeLabel(value) {
  if (!value) return '';
  const normalised = String(value).toLowerCase();
  if (VALUE_TO_LABEL[normalised]) return VALUE_TO_LABEL[normalised];
  if (LEGACY_LABELS[normalised]) return LEGACY_LABELS[normalised];
  // Unknown — title-case it so it isn't an eyesore.
  return normalised.charAt(0).toUpperCase() + normalised.slice(1);
}

/**
 * @returns {boolean} true if `value` is a current, canonical, re-selectable type.
 */
export function isCanonicalJurisdictionType(value) {
  if (!value) return false;
  return JURISDICTION_TYPE_VALUES.includes(String(value).toLowerCase());
}

/**
 * @returns {boolean} true if `value` is a known legacy type that should
 *                    be displayed read-only on existing records.
 */
export function isLegacyJurisdictionType(value) {
  if (!value) return false;
  return Object.prototype.hasOwnProperty.call(
    LEGACY_LABELS,
    String(value).toLowerCase()
  );
}

// ── Jun 2026 — Louisiana Parish vs County ───────────────────────────
// LA has 64 parishes and ZERO counties; every other state has counties
// and no parishes. These helpers make every Type dropdown state-aware
// and let legacy LA records stored as `county` DISPLAY as "Parish"
// without any data change (see scripts/migrate_la_parish_types.py for
// the optional one-time data migration).

/**
 * State-aware jurisdiction type options.
 *   • LA      → hides `county` (Parish is the county-equivalent level)
 *   • non-LA  → hides `parish` (prevents the reverse mistake)
 *   • unknown/empty state → full canonical list (back-compat for
 *     callers that don't know the property state)
 */
export function getJurisdictionTypeOptionsForState(state) {
  const s = String(state || '').trim().toUpperCase();
  if (!s) return JURISDICTION_TYPE_OPTIONS;
  if (s === 'LA') {
    return JURISDICTION_TYPE_OPTIONS.filter((o) => o.value !== 'county');
  }
  return JURISDICTION_TYPE_OPTIONS.filter((o) => o.value !== 'parish');
}

/**
 * State-aware display label. For LA, a stored `county` value renders
 * as "Parish" (legacy records keep working with zero data changes).
 * Everything else falls through to getJurisdictionTypeLabel().
 */
export function getJurisdictionTypeLabelForState(value, state) {
  const s = String(state || '').trim().toUpperCase();
  const v = String(value || '').trim().toLowerCase();
  if (s === 'LA' && v === 'county') return 'Parish';
  return getJurisdictionTypeLabel(value);
}
